import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { LoadingState } from "@/components/base/StateViews";
import Sidebar from "./Sidebar";
import Header from "./Header";
import SubscriptionBanner from "./SubscriptionBanner";
import { useAuth } from "@/contexts/AuthContext";
import { SchoolYearProvider, useSchoolYear } from "@/contexts/SchoolYearContext";

/** Pages accessibles au super-administrateur. */
const PLATFORM_PATHS = ["/dashboard/platform", "/dashboard/activity", "/dashboard/account"];

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Tiroir ouvert (téléphone) : Échap le ferme et la page derrière ne défile plus.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);
  const { user, loading, activeSchool, isPlatformAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-background-100 pt-24">
        <LoadingState label="Ouverture de votre session..." />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.mustChangePassword) return <Navigate to="/changer-mot-de-passe" replace />;
  // Super-administrateur : console de la plateforme uniquement (aucune donnée des écoles).
  if (isPlatformAdmin && !PLATFORM_PATHS.includes(location.pathname.replace(/\/$/, ""))) {
    return <Navigate to="/dashboard/platform" replace />;
  }

  const footerName = activeSchool ? activeSchool.name : "WaitechEdu — Plateforme de gestion scolaire";
  const footerPlace = activeSchool ? ` — ${activeSchool.city}, ${activeSchool.country}` : "";

  return (
    <SchoolYearProvider>
    <div className="min-h-[100dvh] bg-background-100">
      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />
      <div className="lg:pl-[260px]">
        <Header onMenu={() => setMobileOpen(true)} />
        <main className="min-h-[calc(100dvh-4rem)] px-4 py-5 sm:py-6 md:px-6 lg:px-8">
          <div className="mx-auto max-w-[1400px]">
            {activeSchool && <SubscriptionBanner endsAt={activeSchool.subscriptionEndsAt} />}
            <YearPages />
          </div>
        </main>
        <footer className="border-t border-background-200 bg-background-100 px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))] md:px-6">
          <div className="mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-2 text-xs text-foreground-500 sm:flex-row">
            <p>
              © {new Date().getFullYear()} {footerName}
              {footerPlace}
            </p>
            <p className="flex items-center gap-1.5">
              <i className={`${isPlatformAdmin ? "ri-shield-star-line" : "ri-shield-check-line"} text-primary-600`}></i>
              {isPlatformAdmin ? "Console super-admin — toutes les écoles" : "Données cloisonnées par école"}
            </p>
          </div>
        </footer>
      </div>
    </div>
    </SchoolYearProvider>
  );
}

/** Pages de l'année consultée : rechargées quand l'année change ; bandeau quand ce n'est pas l'année active. */
function YearPages() {
  const { consulted, active, version, consult } = useSchoolYear();
  return (
    <>
      {consulted !== active && active && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-warning-200 bg-warning-50 px-4 py-2.5 text-sm text-warning-900">
          <span>
            <i className="ri-history-line mr-1.5"></i>
            Vous consultez l'année <strong>{consulted}</strong> : les pages affichent ses données. L'année active de
            l'école est {active}.
          </span>
          <button type="button" onClick={() => consult(null)} className="press min-h-[2.5rem] text-sm font-semibold underline underline-offset-2 cursor-pointer sm:min-h-0">
            Revenir à {active}
          </button>
        </div>
      )}
      <Outlet key={version} />
    </>
  );
}